import { Renderer2 } from '@angular/core';
import { TOOLTIP_DEFAULTS } from './tooltip.config';
import { TooltipPosition } from './tooltip.interfaces';
import { TooltipConfig } from './tooltip.config';

// ✅ Servicio especializado en renderizado y manipulación del DOM
export class TooltipRenderer {
  
  constructor(private renderer: Renderer2) {}
  
  /**
   * Crea el elemento tooltip con su flecha y configuración
   * Configura automáticamente el contenedor padre para posicionamiento relativo
   */
  createTooltip(content: string, config: TooltipConfig, triggerElement?: HTMLElement): HTMLElement {
    const tooltip = this.renderer.createElement('div');
    const arrow = this.renderer.createElement('div');
    
    // Configurar tooltip principal
    this.renderer.addClass(tooltip, 'tooltip-mcdt');
    this.renderer.setProperty(tooltip, 'innerHTML', content);
    this.renderer.setStyle(tooltip, 'z-index', TOOLTIP_DEFAULTS.Z_INDEX.toString());
    
    // Configurar flecha
    this.renderer.addClass(arrow, 'tooltip-arrow');
    this.renderer.appendChild(tooltip, arrow);
    
    // Configurar automáticamente el contenedor padre para posicionamiento relativo
    if (triggerElement) {
      this.setupParentContainer(triggerElement);
    }
    
    // Añadir soporte de accesibilidad si está habilitado
    if (config.enableA11y) {
      this.addAriaSupport(tooltip);
    }
    
    return tooltip;
  }
  
  /**
   * Configura automáticamente el contenedor padre para posicionamiento relativo
   * Esto es necesario para que position: absolute del tooltip funcione correctamente
   */
  private setupParentContainer(triggerElement: HTMLElement): void {
    const parent = triggerElement.parentElement;
    if (!parent) return;
    
    const computedStyle = window.getComputedStyle(parent);
    const currentPosition = computedStyle.position;
    
    // Solo agregar position-relative si el padre no tiene position definido (static es el default)
    if (currentPosition === 'static') {
      this.renderer.addClass(parent, 'position-relative');
      
      // Debug para Safari - OPTIMIZADO: Solo si debug está habilitado
      /* const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
      if (isSafari && (window as any).__tooltipDebug) {
        console.log('[TooltipMCDT Safari Parent Setup]', {
          triggerElement: triggerElement.tagName,
          parentElement: parent.tagName,
          parentClasses: parent.className,
          originalPosition: currentPosition,
          action: 'Added position-relative'
        });
      } */
    }
  }

  /**
   * Actualiza la posición del tooltip y su flecha - Fixed positioning para mejor control
   */
  updatePosition(element: HTMLElement, position: TooltipPosition): void {
    // Limpiar clases previas de posicionamiento
    ['top', 'bottom', 'left', 'right'].forEach(cls => 
      this.renderer.removeClass(element, cls)
    );
    
    // Aplicar nueva posición - USAR FIXED para posicionamiento absoluto respecto al viewport
    this.renderer.addClass(element, position.placement);
    this.renderer.setStyle(element, 'position', 'fixed');
    this.renderer.setStyle(element, 'left', `${position.x}px`);
    this.renderer.setStyle(element, 'top', `${position.y}px`);
    
    // Z-index alto para asegurar que esté por encima de otros elementos
    this.renderer.setStyle(element, 'z-index', '10000');
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    // Aplicar transform específico según la posición
    if (position.placement === 'top') {
      this.renderer.setStyle(element, 'transform', 'translateY(-100%)');
    } else if (position.placement === 'bottom') {
      this.renderer.setStyle(element, 'transform', 'translateY(0)');
    } else if (position.placement === 'left') {
      this.renderer.setStyle(element, 'transform', 'translateX(-100%)');
    } else if (position.placement === 'right') {
      this.renderer.setStyle(element, 'transform', 'translateX(0)');
    }
    
    // Debug para verificar posicionamiento
    /* if (isSafari && (window as any).__tooltipDebug) {
      console.log('[TooltipMCDT Fixed Position Debug]', {
        placement: position.placement,
        appliedStyles: {
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: position.placement === 'top' ? 'translateY(-100%)' : 'translateY(0)'
        }
      });
    } */
    
    // Actualizar posición de la flecha
    this.updateArrowPosition(element, position);
  }
  
  /**
   * Actualiza la posición específica de la flecha
   */
  private updateArrowPosition(element: HTMLElement, position: TooltipPosition): void {
    const arrow = element.querySelector('.tooltip-arrow') as HTMLElement;
    if (!arrow || !position.arrowPosition) return;
    
    // Limpiar estilos previos de la flecha
    this.renderer.removeStyle(arrow, 'left');
    this.renderer.removeStyle(arrow, 'top');
    this.renderer.removeStyle(arrow, 'transform');
    
    // Aplicar nueva posición de la flecha
    if (position.arrowPosition.x !== undefined) {
      this.renderer.setStyle(arrow, 'left', `${position.arrowPosition.x}px`);
    }
    if (position.arrowPosition.y !== undefined) {
      this.renderer.setStyle(arrow, 'top', `${position.arrowPosition.y}px`);
    }
  }
  
  /**
   * Añade atributos ARIA para accesibilidad
   */
  private addAriaSupport(tooltip: HTMLElement): void {
    const tooltipId = `tooltip-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.renderer.setAttribute(tooltip, 'role', 'tooltip');
    this.renderer.setAttribute(tooltip, 'id', tooltipId);
    this.renderer.setAttribute(tooltip, 'aria-hidden', 'false');
  }
}
